import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AdBanner from '../ads/AdBanner';
import styles from '../../styles/layout/MobileGameplayBanner.module.css';

const MOBILE_MAX_VIEWPORT_WIDTH = 767;

const getViewportWidth = () => {
  if (typeof window === 'undefined') return 0;
  return window.innerWidth || document.documentElement.clientWidth || 0;
};

const resolveAnchorElement = (anchor) => {
  if (!anchor) return null;
  if (Object.prototype.hasOwnProperty.call(anchor, 'current')) {
    return anchor.current || null;
  }
  return typeof anchor.getBoundingClientRect === 'function' ? anchor : null;
};

const anchorIdentityIds = new WeakMap();
let nextAnchorIdentityId = 1;

const getAnchorIdentity = (anchor) => {
  const resolvedAnchor = resolveAnchorElement(anchor) || anchor;
  if (!resolvedAnchor || (typeof resolvedAnchor !== 'object' && typeof resolvedAnchor !== 'function')) {
    return 'null';
  }
  if (!anchorIdentityIds.has(resolvedAnchor)) {
    anchorIdentityIds.set(resolvedAnchor, nextAnchorIdentityId);
    nextAnchorIdentityId += 1;
  }
  return String(anchorIdentityIds.get(resolvedAnchor));
};

export const getMobileGameplayBannerSize = ({
  enabled,
  viewportWidth,
}) => {
  if (!enabled) return null;
  if (!Number.isFinite(viewportWidth) || viewportWidth > MOBILE_MAX_VIEWPORT_WIDTH) {
    return null;
  }
  return 'largeMobileBanner';
};

const MobileGameplayBanner = ({
  enabled = false,
  anchors = [],
  placement = 'mobile-gameplay-banner',
  testId = 'mobile-gameplay-banner',
}) => {
  const [bannerSize, setBannerSize] = useState(null);
  const [mountNode, setMountNode] = useState(null);
  const [measuredPhaseKey, setMeasuredPhaseKey] = useState(null);
  const phaseKey = enabled
    ? anchors.map(getAnchorIdentity).join('|') || 'enabled-without-anchors'
    : 'disabled';
  const latestPhaseKeyRef = useRef(phaseKey);

  latestPhaseKeyRef.current = phaseKey;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setBannerSize(null);
      setMountNode(null);
      setMeasuredPhaseKey('disabled');
      return undefined;
    }

    const scheduledPhaseKey = phaseKey;
    let slotElement = null;
    const frameId = window.requestAnimationFrame(() => {
      if (latestPhaseKeyRef.current !== scheduledPhaseKey) {
        return;
      }

      const anchorEntries = anchors
        .map(resolveAnchorElement)
        .filter(Boolean)
        .filter((element) => typeof element.getBoundingClientRect === 'function')
        .map((element) => ({
          element,
          rect: element.getBoundingClientRect(),
        }))
        .filter(({ rect }) => Number.isFinite(rect?.bottom));

      if (anchorEntries.length === 0) {
        setBannerSize(null);
        setMountNode(null);
        setMeasuredPhaseKey(scheduledPhaseKey);
        return;
      }

      const targetAnchor = anchorEntries.reduce((lowestEntry, entry) => (
        !lowestEntry || entry.rect.bottom > lowestEntry.rect.bottom ? entry : lowestEntry
      ), null);
      const nextBannerSize = getMobileGameplayBannerSize({
        enabled,
        viewportWidth: getViewportWidth(),
      });

      if (!nextBannerSize) {
        setBannerSize(null);
        setMountNode(null);
        setMeasuredPhaseKey(scheduledPhaseKey);
        return;
      }

      slotElement = document.createElement('div');
      slotElement.className = styles.mountPoint;
      targetAnchor.element.insertAdjacentElement('afterend', slotElement);

      setBannerSize(nextBannerSize);
      setMountNode(slotElement);
      setMeasuredPhaseKey(scheduledPhaseKey);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (slotElement?.parentNode) {
        slotElement.parentNode.removeChild(slotElement);
      }
    };
  }, [enabled, phaseKey]);

  if (!bannerSize || !mountNode || measuredPhaseKey !== phaseKey) {
    return null;
  }

  return createPortal(
    <div className={styles.container} data-testid={`${testId}-container`}>
      <div className={styles.inner}>
        <AdBanner
          placement={placement}
          size={bannerSize}
          minViewportWidth={0}
          testId={testId}
        />
      </div>
    </div>,
    mountNode
  );
};

export default MobileGameplayBanner;
